import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useCurrency } from '../../contexts/CurrencyContext';

interface CouponResult {
  valid: boolean;
  discountPercent: number;
  newPrice: number;
  code: string;
}

interface CouponInputProps {
  productPrice: number;
  onApply: (result: CouponResult | null) => void;
}

export function CouponInput({ productPrice, onApply }: CouponInputProps) {
  const { format } = useCurrency();
  const [code,     setCode]     = useState('');
  const [status,   setStatus]   = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  const [result,   setResult]   = useState<CouponResult | null>(null);
  const debounceRef             = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!(code ?? '').trim()) { setStatus('idle'); setResult(null); onApply(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => validate((code ?? '').trim().toUpperCase()), 600);
    return () => clearTimeout(debounceRef.current);
  }, [code]);

  async function validate(c: string) {
    setStatus('loading');
    const { data } = await supabase
      .from('discount_coupons')
      .select('discount_percent, active, expires_at, max_redemptions, redemptions_count')
      .eq('code', c)
      .maybeSingle();

    if (!data || !data.active) { setStatus('invalid'); setResult(null); onApply(null); return; }
    if (data.expires_at && new Date(data.expires_at) < new Date()) { setStatus('invalid'); setResult(null); onApply(null); return; }
    if (data.max_redemptions && data.redemptions_count >= data.max_redemptions) { setStatus('invalid'); setResult(null); onApply(null); return; }

    const discount   = data.discount_percent / 100;
    const newPrice   = productPrice * (1 - discount);
    const r: CouponResult = { valid: true, discountPercent: data.discount_percent, newPrice, code: c };
    setStatus('valid');
    setResult(r);
    onApply(r);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text" className="form-input" value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="CÓDIGO DE DESCONTO"
          style={{ letterSpacing: '0.08em', textTransform: 'uppercase', flex: 1 }}
          maxLength={32}
        />
        {status === 'loading' && (
          <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', paddingRight: '0.5rem' }}>
            …
          </span>
        )}
      </div>

      {status === 'valid' && result && (
        <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-caption)', color: '#4ade80' }}>
          ✓ Cupom aplicado, {result.discountPercent}% de desconto
          <span style={{ float: 'right', fontWeight: 700 }}>{format(result.newPrice)}</span>
        </div>
      )}

      {status === 'invalid' && code.length > 0 && (
        <p style={{ marginTop: '0.4rem', fontSize: 'var(--text-caption)', color: '#fca5a5' }}>
          Cupom inválido ou expirado
        </p>
      )}
    </div>
  );
}
